import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('control_panel_render_error', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });
  }

  reset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--bg-0)',
        padding: 24,
      }}>
        <div className="card" style={{ maxWidth: 720, width: '100%' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>💥</div>
          <h1 style={{ fontSize: 28, margin: '0 0 8px' }}>Something went wrong</h1>
          <p style={{ color: 'var(--text-2)', margin: '0 0 16px', lineHeight: 1.6 }}>
            The control panel hit an unexpected render error. This usually means a page component or chart
            crashed while rendering. You can retry, go back to the dashboard, or reload the app.
          </p>

          {this.state.error && (
            <div style={{
              background: 'var(--bg-2)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 12,
              marginBottom: 16,
              fontFamily: 'monospace',
              fontSize: 12,
              color: 'var(--text-1)',
              overflowX: 'auto',
            }}>
              {this.state.error.message}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="btn btn-primary" onClick={this.reset}>Try Again</button>
            <button className="btn btn-ghost" onClick={() => window.location.reload()}>Reload App</button>
            <Link className="btn btn-ghost" to="/">Go to Dashboard</Link>
          </div>
        </div>
      </div>
    );
  }
}
